import React, { useState } from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

// Touch-first answer bodies for the interactive question types.
// All of them report their current answer object via onChange(answerObjOrNull).

const chipSx = (state) => ({
  borderRadius: "999px",
  padding: "6px 14px",
  border: "none",
  fontFamily: "inherit",
  fontWeight: 700,
  fontSize: "0.92rem",
  cursor: "pointer",
  background: state === "selected" ? "#0080dc" : state === "used" ? "#e0e0e0" : "#eef4fa",
  color: state === "selected" ? "#fff" : state === "used" ? "#9e9e9e" : "#0d47a1",
  transition: "all 150ms",
});

// ---- المطابقة: اكبس كلمة من الأعلى ثم اكبس مكانها ----
export const MatchingBody = ({ payload, onChange }) => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState(null); // right value currently "in hand"
  const [assignments, setAssignments] = useState({}); // leftIndex -> right value

  const report = (next) => {
    onChange(Object.keys(next).length === payload.lefts.length ? { assignments: next } : null);
  };

  const usedRights = new Set(Object.values(assignments));

  return (
    <Box>
      <Typography fontSize="0.8rem" color="text.secondary" mb={1}>{t("wb_matching_hint")}</Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: 2 }}>
        {payload.rights.map((right, index) => (
          <Box
            key={index}
            component="button"
            type="button"
            disabled={usedRights.has(right)}
            onClick={() => setPicked(picked === right ? null : right)}
            sx={chipSx(picked === right ? "selected" : usedRights.has(right) ? "used" : "idle")}
          >
            {right}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {payload.lefts.map((left, leftIndex) => (
          <Box
            key={leftIndex}
            onClick={() => {
              const next = { ...assignments };
              if (next[leftIndex] !== undefined) {
                delete next[leftIndex]; // tap again to release
              } else if (picked) {
                next[leftIndex] = picked;
                setPicked(null);
              }
              setAssignments(next);
              report(next);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 14px",
              borderRadius: "12px",
              cursor: "pointer",
              background: assignments[leftIndex] ? "#e3f2fd" : "#f5f5f5",
              border: picked && assignments[leftIndex] === undefined ? "2px dashed #0080dc" : "2px solid transparent",
            }}
          >
            <Typography fontWeight={700} sx={{ flex: 1 }}>{left}</Typography>
            <Typography fontWeight={700} color={assignments[leftIndex] ? "primary" : "text.disabled"}>
              {assignments[leftIndex] || "؟"}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ---- التصنيف: اكبس عنصر ثم اكبس مجموعته ----
export const CategorizeBody = ({ payload, onChange }) => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState(null);
  const [placements, setPlacements] = useState({}); // item -> catIndex

  const report = (next) => {
    onChange(Object.keys(next).length === payload.items.length ? { placements: next } : null);
  };

  const pool = payload.items.filter((item) => placements[item] === undefined);

  return (
    <Box>
      <Typography fontSize="0.8rem" color="text.secondary" mb={1}>{t("wb_categorize_hint")}</Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: 2, minHeight: 38 }}>
        {pool.map((item) => (
          <Box
            key={item}
            component="button"
            type="button"
            onClick={() => setPicked(picked === item ? null : item)}
            sx={chipSx(picked === item ? "selected" : "idle")}
          >
            {item}
          </Box>
        ))}
        {pool.length === 0 && <Typography fontSize="0.8rem" color="text.secondary">✓</Typography>}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "8px" }}>
        {payload.categories.map((name, catIndex) => (
          <Box
            key={catIndex}
            onClick={() => {
              if (!picked) return;
              const next = { ...placements, [picked]: catIndex };
              setPlacements(next);
              setPicked(null);
              report(next);
            }}
            sx={{
              borderRadius: "12px",
              border: picked ? "2px dashed #0080dc" : "2px solid rgba(0,0,0,0.1)",
              padding: "8px",
              minHeight: 90,
              cursor: picked ? "pointer" : "default",
              background: "#fafafa",
            }}
          >
            <Typography fontWeight={800} fontSize="0.9rem" mb={1} textAlign="center">{name}</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {payload.items.filter((item) => placements[item] === catIndex).map((item) => (
                <Chip
                  key={item}
                  size="small"
                  label={item}
                  onDelete={() => {
                    const next = { ...placements };
                    delete next[item];
                    setPlacements(next);
                    report(next);
                  }}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ---- الترتيب: أسهم فوق/تحت ----
export const OrderingBody = ({ payload, onChange }) => {
  const { t } = useTranslation();
  const [order, setOrder] = useState(payload.items);

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
    onChange({ order: next });
  };

  // an untouched list is still a valid submission
  React.useEffect(() => { onChange({ order: payload.items }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Typography fontSize="0.8rem" color="text.secondary" mb={1}>{t("wb_ordering_hint")}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {order.map((item, index) => (
          <Box key={item} sx={{ display: "flex", alignItems: "center", gap: "8px", background: "#f5f5f5", borderRadius: "12px", padding: "6px 10px" }}>
            <Typography fontWeight={800} color="primary" sx={{ minWidth: 22, textAlign: "center" }}>{index + 1}</Typography>
            <Typography fontWeight={600} sx={{ flex: 1 }}>{item}</Typography>
            <IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)}>⬆️</IconButton>
            <IconButton size="small" disabled={index === order.length - 1} onClick={() => move(index, 1)}>⬇️</IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ---- ضع دائرة على الصحيح: المس المكان على الصورة ----
export const HotspotBody = ({ payload, onChange, solution }) => {
  const { t } = useTranslation();
  const [spot, setSpot] = useState(null);

  return (
    <Box>
      <Typography fontSize="0.8rem" color="text.secondary" mb={1}>{t("wb_hotspot_hint")}</Typography>
      <Box
        sx={{ position: "relative", borderRadius: "12px", overflow: "hidden", cursor: "crosshair", userSelect: "none" }}
        onClick={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const next = {
            x: Math.round(((event.clientX - rect.left) / rect.width) * 1000) / 1000,
            y: Math.round(((event.clientY - rect.top) / rect.height) * 1000) / 1000,
          };
          setSpot(next);
          onChange(next);
        }}
      >
        <img src={payload.image} alt="" draggable={false} style={{ width: "100%", display: "block" }} />
        {spot && (
          <Box sx={{
            position: "absolute",
            left: `${spot.x * 100}%`,
            top: `${spot.y * 100}%`,
            transform: "translate(-50%, -50%)",
            width: 26, height: 26,
            border: "4px solid #0080dc",
            borderRadius: "50%",
            background: "rgba(0,128,220,0.2)",
            pointerEvents: "none",
          }} />
        )}
        {solution && (
          <Box sx={{
            position: "absolute",
            left: `${solution.x * 100}%`,
            top: `${solution.y * 100}%`,
            transform: "translate(-50%, -50%)",
            width: `${solution.r * 2 * 100}%`,
            aspectRatio: "1",
            border: "3px solid #2e7d32",
            borderRadius: "50%",
            background: "rgba(46,125,50,0.15)",
            pointerEvents: "none",
          }} />
        )}
      </Box>
    </Box>
  );
};
